// The desktop thread pane (ADR-003): a resizable side pane that keeps the conversation visible.
// The thread is a conversation of its own — root pinned first, replies in reading order, its own
// composer replying into it.
import { useEffect, useMemo, useState } from 'react';
import { data } from '../../bridge/client';
import type { ChannelPage } from '../../bridge/protocol';
import { userDisplayName } from '../../core/channelListDerivation';
import { PostState, type PostRecord } from '../../core/records';
import { Icon } from '../../design/icons';
import { t } from '../../i18n/i18n';
import { useLive, useMinuteClock } from '../hooks';
import { buildConversation } from '../models/conversationModel';
import { Composer } from './Composer';
import { useQuickReactions } from './Conversation';
import { useRowActions } from './rowActions';
import type { BodyContext } from './MessageBody';
import { MessageRow, type RowActions } from './MessageRow';

export function ThreadPane({
  rootId,
  me,
  myUsername,
  onClose,
  onOpenUser,
  onOpenUserId,
  onToast,
  teamName,
  width,
}: {
  rootId: string;
  me: string;
  myUsername: string | null;
  onClose(): void;
  onOpenUser(username: string, anchor?: { x: number; y: number }): void;
  onOpenUserId(userId: string, anchor?: { x: number; y: number }): void;
  onToast(message: string, tone?: 'info' | 'error'): void;
  teamName: string;
  width: number;
}) {
  const [echoes, setEchoes] = useState<PostRecord[]>([]);
  const now = useMinuteClock();
  const quickReactions = useQuickReactions();

  useEffect(() => {
    setEchoes([]);
    void data.call('loadThread', rootId);
  }, [rootId]);

  const { value: page } = useLive<ChannelPage>(() => data.call('threadPage', rootId), ['posts', 'postFiles', 'postReactions', 'users', 'postSeenSummaries', 'customEmoji'], [rootId]);
  const current = page && page.posts[0]?.id === rootId ? page : page && page.posts.length === 0 ? null : page;
  const model = useMemo(() => (current ? buildConversation(current, me, null, now, echoes, true) : null), [current, me, now, echoes]);

  useEffect(() => {
    if (!current || !echoes.length) return;
    const stored = new Set(current.posts.flatMap((x) => [x.id, x.pendingId ?? '']));
    const left = echoes.filter((e) => !stored.has(e.id));
    if (left.length !== echoes.length) setEchoes(left);
  }, [current, echoes]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const actions: RowActions = useRowActions({ onOpenThread: () => undefined, onOpenUserId, onToast, teamName, quickReactions });
  const ctx: BodyContext = useMemo(
    () => ({ knownUsernames: model?.knownUsernames ?? new Set(), currentUsername: myUsername, customEmoji: current?.customEmoji ?? {}, onMention: (u: string, a?: { x: number; y: number }) => onOpenUser(u, a) }),
    [model?.knownUsernames, myUsername, current?.customEmoji, onOpenUser],
  );
  const channelId = current?.channelId ?? '';

  const send = (message: string) => {
    if (!channelId) return;
    const pendingId = crypto.randomUUID().replace(/-/g, '');
    const createAt = Date.now();
    setEchoes((e) => [
      ...e,
      { id: pendingId, channelId, userId: me, createAt, updateAt: createAt, deleteAt: 0, message, rootId, pendingId, state: PostState.Pending, type: '', props: null, isPinned: false, editAt: 0, embedJson: null, priority: '' },
    ]);
    void data.call('send', channelId, message, rootId, pendingId, createAt);
  };

  return (
    <aside className="conversation-pane thread-pane" style={{ flex: `0 0 ${width}px`, borderLeft: '1px solid var(--border)' }} aria-label={t('channel.threadTitle')}>
      <div className="wallpaper" />
      <header className="conversation-header">
        <span style={{ color: 'var(--text-muted)', display: 'grid' }}>
          <Icon name="threads" size={18} />
        </span>
        <div className="title-block">
          <h1>{t('channel.threadTitle')}</h1>
        </div>
        <button className="icon-button" onClick={onClose} title={t('common.close')} aria-label={t('common.close')}>
          <Icon name="x" size={17} />
        </button>
      </header>
      <div className="timeline" style={{ flexDirection: 'column' }}>
        <div className="timeline-inner">
          {model
            ? model.rows.map((row) => {
                const author = model.usersById.get(row.post.userId);
                return (
                  <MessageRow
                    key={row.post.id}
                    row={row}
                    authorName={model.authorName(row.post)}
                    authorVersion={author?.lastPictureUpdate ?? 0}
                    presence={current?.statuses[row.post.userId] ?? null}
                    isOwn={row.post.userId === me}
                    files={current?.files[row.post.id]}
                    chips={model.chips.get(row.post.id)}
                    footer={undefined}
                    footerNames={(id) => ({ name: model.usersById.get(id) ? userDisplayName(model.usersById.get(id)!) : '?', version: 0 })}
                    seen={model.seen.get(row.post.id) ?? null}
                    saved={(current?.saved ?? []).includes(row.post.id)}
                    ctx={ctx}
                    actions={actions}
                    now={now}
                    inThread
                  />
                );
              })
            : null}
        </div>
      </div>
      {channelId ? (
        <Composer
          key={rootId}
          channelKey={rootId}
          initialDraft={current?.draft ?? ''}
          placeholder={t('channel.threadReplyPlaceholder')}
          participants={model ? [...model.usersById.values()].filter((u) => u.id !== me) : []}
          customEmojiNames={Object.keys(current?.customEmoji ?? {})}
          onSend={send}
          onDraft={(m) => void data.call('saveDraft', channelId, rootId, m)}
          onTyping={() => void data.call('userTyping', channelId, rootId)}
        />
      ) : null}
    </aside>
  );
}
